export type ConfirmationSpokenMetadata = {
  ref: { kind: string; id: string };
  digest: string;
  lang: string;
  lines: {
    approving: string;
    rejecting: string;
    executed: string;
    failed: string;
    unknown: string;
  };
};
export type SpokenPart = {
  phase: 'progress' | 'answer';
  text: string;
  sequence: number;
  confirmation?: ConfirmationSpokenMetadata;
};
export type SpokenPartOrigin = { messageId: string; resumed: boolean };

type VoiceOutputData = {
  version: 1;
  channel: 'spoken';
  messageId: string;
  part: SpokenPart;
};

const confirmationMetadata = (value: unknown): ConfirmationSpokenMetadata | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const candidate = value as Partial<ConfirmationSpokenMetadata>;
  const lines = candidate.lines;
  if (
    !candidate.ref ||
    typeof candidate.ref !== 'object' ||
    typeof candidate.ref.kind !== 'string' ||
    typeof candidate.ref.id !== 'string' ||
    typeof candidate.digest !== 'string' ||
    typeof candidate.lang !== 'string' ||
    !lines ||
    typeof lines !== 'object'
  )
    return undefined;
  // voice-structural: confirmation line fields carried in the spoken metadata contract
  const confirmationLineKeys = ['approving', 'rejecting', 'executed', 'failed', 'unknown'];
  if (!confirmationLineKeys.every((key) => typeof lines[key as keyof typeof lines] === 'string'))
    return undefined;
  return candidate as ConfirmationSpokenMetadata;
};

/** Validates transient transport data and fences it to the active assistant message. */
export function createSpokenChannelBridge(
  onPart: (part: SpokenPart, origin: SpokenPartOrigin) => void,
) {
  let messageId: string | null = null;
  const seen = new Set<string>();
  return {
    begin(nextMessageId: string) {
      if (messageId !== nextMessageId) {
        messageId = nextMessageId;
        seen.clear();
      }
    },
    accept(value: unknown, resumed = false): boolean {
      if (!value || typeof value !== 'object') return false;
      const candidate = value as Partial<VoiceOutputData>;
      const part = candidate.part;
      if (
        candidate.version !== 1 ||
        candidate.channel !== 'spoken' ||
        typeof candidate.messageId !== 'string' ||
        !messageId ||
        candidate.messageId !== messageId ||
        !part ||
        (part.phase !== 'progress' && part.phase !== 'answer') ||
        typeof part.text !== 'string' ||
        part.text.trim().length === 0 ||
        !Number.isInteger(part.sequence) ||
        part.sequence < 0
      )
        return false;
      const key = `${messageId}:${part.sequence}`;
      if (seen.has(key)) return false;
      seen.add(key);
      onPart(
        {
          phase: part.phase,
          text: part.text,
          sequence: part.sequence,
          ...(confirmationMetadata(part.confirmation)
            ? { confirmation: confirmationMetadata(part.confirmation) }
            : {}),
        },
        { messageId, resumed },
      );
      return true;
    },
  };
}
